import { Fragment, h } from 'preact';
import { useEffect } from 'preact/compat';

import { ConfirmationStepEnum } from './Confirmation';
import {
	CartActionType, CartReducerAction, formatCurrency, CartState,
} from '../../hooks/cart';
import { validConfirmTransition } from '../../hooks/transitionGaurds';
import { scrollToSelector, isMobile } from '../../lib/helpers';
import { WPConfig } from '../../types';
import Spinner from '../Spinner';

/**
 * Button navigation through confirmation flow.
 * Handles conditional upsell navigation.
 */
const ConfirmNavigation = ({
	state,
	setState,
	dispatch,
	isUpsellValid,
	backTarget,
	theme,
	total,
	isSubmitting,
	cart,
}: {
	state: ConfirmationStepEnum;
	setState: (state: ConfirmationStepEnum) => void;
	dispatch: (action: CartReducerAction) => void;
	isUpsellValid: boolean;
	backTarget: ConfirmationStepEnum;
	theme: WPConfig;
	total: number;
	isSubmitting: boolean;
	cart: CartState;
}) => {
	/**
	 * On mobile force scroll to the top of the inventory component.
	 * Only on upsell and details.
	 */
	useEffect(() => {
		if (state !== ConfirmationStepEnum.UPSELL && state !== ConfirmationStepEnum.DETAILS) {
			return;
		}
		if (isMobile()) {
			scrollToSelector('.ea-inventory', -50);
		}
	}, [state]);

	return (
		<div className="ea-confirm-nav">
			<div className="ea-confirm-nav__inner">
				{/**
					 * Details.
					 * - hasUpsell continue to upsell
					 * - !hasUpsell submit donation.
					 */
					(isUpsellValid && state === ConfirmationStepEnum.DETAILS) && (
						<div>
							<button
								type="button"
								className="ea-btn ea-btn--lg ea-btn--invert"
								onClick={(e) => {
									e.preventDefault();
									setState(ConfirmationStepEnum.UPSELL);
								}}
							>
								Continue
							</button>
						</div>
					)
				}
				{/**
					 * Upsell.
					 * Stacked buttons.
					 * 1 - Add upsell amount to cart, continue.
					 * 2 — Remove/skip upsell amount, continue.
					 */
					state === ConfirmationStepEnum.UPSELL && (
						<Fragment>
							{
								!cart.selections.has(theme.upsell.target.id) ? (
									<div>
										<button
											type="button"
											/**
											 * Add upsell to cart.
											 */
											className="ea-btn ea-btn--lg ea-btn"
											onClick={(e) => {
												e.preventDefault();
												dispatch({
													type: CartActionType.MANUAL_INPUT,
													payload: {
														...theme.upsell.target,
														amount: theme.upsell.amount,
														isUpsell: true,
													},
												});
												setState(ConfirmationStepEnum.FINAL_CONFIRM);
											}}
										>
											{`Add ${formatCurrency(theme.upsell.amount)}`}
										</button>
									</div>
								) : null
							}
							<div>
								<button
									type="button"
									/**
									 * Continue btn.
									 *
									 * Visual que, if upsell is in cart guide user to complete.
									 * Btn tracks current cart total w/ upsell amount.
									 */
									className={[
										'ea-btn ea-btn--lg',
										cart.selections.has(theme.upsell.target.id) ? 'ea-btn' : 'ea-btn--invert',
									].join(' ')}
									onClick={(e) => {
										e.preventDefault();
										setState(ConfirmationStepEnum.FINAL_CONFIRM);
									}}
								>
									Keep my donation
									{` ${formatCurrency(total)}`}
								</button>
							</div>
						</Fragment>
					)
				}
				{/**
					 * Final Confirm
					 * Submit button.
					 */
					(state === ConfirmationStepEnum.FINAL_CONFIRM
						|| (!isUpsellValid && state === ConfirmationStepEnum.DETAILS)) && (
						<div>
							<button
								type="submit" // Action.
								className="ea-btn ea-btn--lg"
								disabled={!validConfirmTransition() || isSubmitting}
							>
								{isSubmitting ? (
									<Spinner />
								) : (
									<span>
										Complete My
										{` ${formatCurrency(total)} `}
										Donation
									</span>
								)}
							</button>
						</div>
					)
				}
			</div>
			{/**
				 * 'Back' navigation.
				 * Appears after details.
				 */
				(state === ConfirmationStepEnum.UPSELL
					|| state === ConfirmationStepEnum.FINAL_CONFIRM) && (
					<div className="ea-confirm-nav__back">
						<button
							type="button"
							className="ea-btn-reset ea-btn-reset--linklike"
							onClick={(e) => {
								e.preventDefault();
								setState(backTarget);
							}}
						>
							Back
						</button>
					</div>
				)
			}
		</div>
	);
};

export default ConfirmNavigation;
